import React from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useGetAccountAuthenticated } from '../session/swr/useGetAccountAuthenticated';
import { useLanguage } from '../common/GeneralUtils';
import { getActiveLocations, getEffectivePageLocationId, getLocationName } from '../common/filter/locationGrouping';
import { getOrderPages } from '../utils/AccountUtils';

type LocationChangeDialogProps = {
  open: boolean;
  userName: string;
  fromLocationId: string | null;
  fromIsFloating: boolean;
  toLocationId: string | null;
  toIsFloating: boolean;
  qualifiedOrderPageIds: string[];
  onCancel: () => void;
  onConfirm: () => void;
};

export const LocationChangeDialog = ({
  open,
  userName,
  fromLocationId,
  fromIsFloating,
  toLocationId,
  toIsFloating,
  qualifiedOrderPageIds,
  onCancel,
  onConfirm,
}: LocationChangeDialogProps) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { account } = useGetAccountAuthenticated({});

  const activeLocations = getActiveLocations(account);
  const getNameById = (locationId: string | null) => {
    const location = activeLocations.find((activeLocation) => activeLocation.id === locationId);
    return location ? getLocationName(location, language) : '';
  };

  const orderPages = getOrderPages(account, true);
  const isRemovingFromOtherLocations = fromIsFloating || fromLocationId === null;
  const removedCount = orderPages.filter((page) => {
    if (!qualifiedOrderPageIds.includes(page.id)) {
      return false;
    }
    const pageLocationId = getEffectivePageLocationId(page.location_id, account);
    return isRemovingFromOtherLocations
      ? pageLocationId !== null && pageLocationId !== toLocationId
      : pageLocationId === fromLocationId;
  }).length;
  const addedCount = orderPages.filter(
    (page) => getEffectivePageLocationId(page.location_id, account) === toLocationId,
  ).length;
  const activityPlural = account.activity_label_plural?.[language] ?? t('passes.activity_fallback_plural');

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="xs">
      <DialogTitle>
        {toIsFloating
          ? t('users.location_dialog.title_floating', { name: userName })
          : t('users.location_dialog.title', { name: userName, location: getNameById(toLocationId) })}
      </DialogTitle>
      <DialogContent>
        <Typography component="ul" variant="body2" sx={{ m: 0, paddingInlineStart: 2.5, '& li + li': { mt: 1 } }}>
          {toIsFloating ? (
            <li>{t('users.location_dialog.floating_keeps', { activityPlural })}</li>
          ) : (
            <>
              {removedCount > 0 && (
                <li>
                  {isRemovingFromOtherLocations
                    ? t('users.location_dialog.removed_other_locations', { count: removedCount, activityPlural })
                    : t('users.location_dialog.removed', {
                        count: removedCount,
                        activityPlural,
                        location: getNameById(fromLocationId),
                      })}
                </li>
              )}
              <li>
                {isRemovingFromOtherLocations
                  ? t('users.location_dialog.kept_at_location', { activityPlural, location: getNameById(toLocationId) })
                  : t('users.location_dialog.added', {
                      count: addedCount,
                      activityPlural,
                      location: getNameById(toLocationId),
                    })}
              </li>
              <li>{t('users.location_dialog.without_location', { activityPlural })}</li>
            </>
          )}
          <li>{t('users.location_dialog.assignments')}</li>
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button sx={{ textTransform: 'none' }} onClick={onCancel}>
          {t('cancel')}
        </Button>
        <Button variant="contained" sx={{ textTransform: 'none' }} onClick={onConfirm}>
          {t(toIsFloating ? 'users.location_dialog.confirm_floating' : 'users.location_dialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
